import React, { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  CalendarDays,
  Layers,
  ListChecks,
  Zap,
  MessageSquare,
  FileText,
  Users,
  User,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import { authApi, type AdminAuthUser } from '../../services/api/auth.api';
import { canSeeAdminSection } from '../../utils/adminPermissions';
import type { AdminPermissions } from '@business-game/shared';

const navItems: Array<{
  to: string;
  label: string;
  icon: LucideIcon;
  section?: keyof AdminPermissions['sections'];
  usersOnly?: boolean;
}> = [
  { to: '/admin/sessions', label: 'Sessions', icon: CalendarDays, section: 'sessions' },
  { to: '/admin/programs', label: 'Programmes', icon: Layers, section: 'programs' },
  { to: '/admin/criterias', label: 'Critères', icon: ListChecks, section: 'criterias' },
  { to: '/admin/impacts', label: 'Impacts', icon: Zap, section: 'impacts' },
  { to: '/admin/verbatims', label: 'Verbatims', icon: MessageSquare, section: 'verbatims' },
  { to: '/admin/player-texts', label: 'Textes joueur', icon: FileText, section: 'playerTexts' },
  { to: '/admin/users', label: 'Utilisateurs', icon: Users, usersOnly: true },
  { to: '/admin/profile', label: 'Profil', icon: User },
];

const SIDEBAR_WIDTH = 'w-[4.25rem]';
const SIDEBAR_PL = 'pl-[4.25rem]';

export const AdminLayout: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<AdminAuthUser | null>(null);

  useEffect(() => {
    authApi.me().then(setCurrentUser).catch(() => setCurrentUser(null));
  }, []);

  useEffect(() => {
    const onProfileUpdated = (e: CustomEvent<AdminAuthUser>) => {
      setCurrentUser(e.detail);
    };
    window.addEventListener('admin-profile-updated', onProfileUpdated as EventListener);
    return () => window.removeEventListener('admin-profile-updated', onProfileUpdated as EventListener);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('teamlab_admin_token');
    window.location.href = '/admin/login';
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      {/* Sidebar - indigo/violet style */}
      <aside
        className={`fixed left-0 top-0 z-20 flex ${SIDEBAR_WIDTH} h-full flex-col items-center border-r border-indigo-200/60 bg-white/90 py-4 shadow-xl shadow-indigo-500/5 backdrop-blur-xl`}
      >
        <div className="mb-6 flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-600 text-xs font-black text-white" title="Admin">
          A
        </div>

        <nav className="flex flex-1 flex-col items-center gap-1.5">
          {navItems
            .filter((item) => {
              if (item.usersOnly) return !!currentUser?.canManageUsers;
              if (!item.section) return true;
              return canSeeAdminSection(currentUser, item.section);
            })
            .map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  title={item.label}
                  className={({ isActive }) =>
                    `flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-200 ${
                      isActive
                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                        : 'text-slate-500 hover:bg-indigo-50 hover:text-indigo-900'
                    }`
                  }
                >
                  <Icon className="h-5 w-5 shrink-0" />
                </NavLink>
              );
            })}
        </nav>

        <div className="mt-auto flex flex-col items-center gap-2 border-t border-slate-200 pt-4">
          {currentUser && (
            <NavLink
              to="/admin/profile"
              title={currentUser.displayName || currentUser.email}
              className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-white transition-colors hover:border-indigo-300"
            >
              {currentUser.logoUrl ? (
                <img src={currentUser.logoUrl} alt="" className="h-full w-full object-contain" />
              ) : (
                <span className="text-sm font-bold text-indigo-600">
                  {(currentUser.displayName || currentUser.login || '?')[0].toUpperCase()}
                </span>
              )}
            </NavLink>
          )}
          <button
            onClick={handleLogout}
            title="Se déconnecter"
            className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </aside>

      <div className={`fixed left-[4.25rem] top-0 right-0 bottom-0 -z-10 overflow-hidden bg-slate-100`}>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:24px_24px]" />
        <div className="absolute right-0 top-0 h-[400px] w-[400px] rounded-full bg-indigo-500/10 blur-[80px] pointer-events-none" />
        <div className="absolute left-0 bottom-0 h-[300px] w-[300px] rounded-full bg-blue-500/10 blur-[60px] pointer-events-none" />
      </div>

      <main className={`min-h-screen ${SIDEBAR_PL} pt-6 pr-6 pb-6 lg:pt-8 lg:pr-8 lg:pb-8`}>
        {currentUser?.isImpersonating && (
          <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-black uppercase tracking-wider text-amber-700">Mode impersonation</div>
              <div className="text-sm text-amber-800 mt-1">
                Connecté en tant que <span className="font-bold">{currentUser.displayName}</span> via{' '}
                <span className="font-bold">{currentUser.impersonatorDisplayName}</span>.
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  const result = await authApi.stopImpersonation();
                  localStorage.setItem('teamlab_admin_token', result.token);
                  window.location.href = '/admin';
                } catch {
                  alert('Erreur lors du retour au super admin');
                }
              }}
              className="inline-flex items-center justify-center rounded-xl bg-amber-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-700"
            >
              Revenir au super admin
            </button>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
};
